import { useQuery } from '@tanstack/vue-query'
import { type MaybeRef, get } from '@vueuse/core'
import { computed } from 'vue'

import type { CostCenter } from '@/interfaces/costCenter'
import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import type { PaginatedResponse } from '@/interfaces/paginatedResponse'
import axios from '@/lib/axios'

/**
 * Centros de costo de una empresa, con `companyId` REACTIVO.
 *
 * Ya existe `internal/modules/companies/composables/useCompanyCostCenter`, pero
 * ese recibe los params como objeto plano: la query key se arma una sola vez y
 * cambiar de empresa no vuelve a pedir. En el alta de usuario la empresa se
 * elige dentro del formulario, así que hace falta que la lista siga ese cambio.
 *
 * ⚠️ El endpoint responde PAGINADO (`{ data, links, meta }`), no un array. El
 * composable interno lo tipa como `CostCenter[]` y por eso revienta con
 * "costCenters.filter is not a function" en cuanto alguien lo recorre. Acá se
 * lee `.data` y además se pide una página grande, igual que hace
 * `useCompanyDepartment`, porque los consumidores son dropdowns que esperan la
 * lista completa y no tienen paginación.
 *
 * El filtrado por departamento se hace en el consumidor: es una lista corta y
 * evita un endpoint nuevo. La alternativa (`/api/booking/travelers/{id}/cost-centers`)
 * necesita un usuario ya creado, que es justo lo que todavía no existe.
 */
const DROPDOWN_PER_PAGE = 100

const getCompanyCostCenters = async (
	companyId: number
): Promise<CostCenter[]> => {
	const { data } = await axios.get<PaginatedResponse<CostCenter>>(
		`/api/companies/${companyId}/cost-centers`,
		{
			params: { per_page: DROPDOWN_PER_PAGE },
			errorHandling: false,
		} as CustomAxiosRequestConfig
	)

	// Defensivo a propósito: este endpoint ya cambió de forma una vez (array →
	// paginado) y quien lo consuma después no debería enterarse por un crash.
	if (Array.isArray(data)) return data

	return Array.isArray(data?.data) ? data.data : []
}

const useCompanyCostCenters = (companyId: MaybeRef<number | undefined>) => {
	const id = computed(() => get(companyId))

	const { data, isLoading, isFetching, refetch, error } = useQuery(
		['companyCostCenters', id],
		() => getCompanyCostCenters(id.value!),
		{
			// Reactivo: sin empresa no hay a qué pedirle centros de costo.
			enabled: computed(() => !!id.value),
			retry: false,
			staleTime: 5 * 60 * 1000,
		}
	)

	const costCenters = computed<CostCenter[]>(() =>
		Array.isArray(data.value) ? data.value : []
	)

	/** Los centros de costo del departamento dado. Sin departamento, ninguno. */
	const costCentersOfDepartment = (departmentId: number | undefined) =>
		departmentId ?
			costCenters.value.filter(
				(costCenter) => costCenter.departmentId === Number(departmentId)
			)
		:	[]

	return {
		costCenters,
		costCentersOfDepartment,
		isLoading,
		isFetching,
		error,
		refetch,
	}
}

export default useCompanyCostCenters
